import React, { useState, useEffect } from 'react';
import { Card, Grid, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ReportHttpServices from 'src/services/reportsHttpService';
import { internalReportModuleURL } from 'src/services/urlService';
import BestPostCardDetails from 'src/components/reports/summary-cards/bestPostCard';
import TableDonutChart from 'src/components/reports/summary-cards/summaryTableDonutCard';
import SummaryCommonCard from 'src/components/reports/summary-cards/summaryCommonCard';
import NoReportCard from 'src/components/reports/summary-cards/noReportCard';
import { useLocation } from 'react-router-dom';

const EngagementNestedCard = ({ connectedProfileId, startDate, endDate, viewBy, engagementSummary, serviceName }) => {
  const theme = useTheme();
  const [isLoading, setLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlBrandId = urlParams.get('brand_id');
  const urlToken = urlParams.get('token');
  const [postEngagementSummary, setPostEngagementSummary] = useState({
    totalPosts: null,
    totalEngagement: null,
    bestPost: null,
    engagementPostType: null,
  });
  const { showToast } = useToaster();

  useEffect(() => {
    if (connectedProfileId) {
      getEngagementSummary();
    }
  }, [connectedProfileId, startDate, endDate]);

  const getEngagementSummary = () => {
    let body = {
      connected_profile_id: connectedProfileId,
      report_id: engagementSummary.id,
      date_range: {
        from: startDate,
        to: endDate,
      },
      view_by: 'day',
    };
    let getProps = {
      url: `${internalReportModuleURL.scheduledReports}${serviceName}`,
      body: body,
      brandId: urlBrandId,
      token: urlToken,
      successCallback: postEngagementSuccessCallback,
      failureCallback: postEngagementFailureCallback,
    };
    ReportHttpServices.Post(getProps);
  };

  const postEngagementFailureCallback = (message) => {
    setPostEngagementSummary({
      totalPosts: null,
      totalEngagement: null,
      bestPost: null,
      engagementPostType: null,
    });
    if (message) showToast(message, 'e');
    setLoading(false);
  };

  const postEngagementSuccessCallback = (data) => {
    const responseData = data[0]?.data || {};
    setPostEngagementSummary({
      totalPosts: responseData?.total_posts,
      totalEngagement: responseData?.total_engagement,
      bestPost: responseData?.best_post,
      engagementPostType: responseData?.engagement_post_type,
    });
    setLoading(false);
  };

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        border: '1px solid #e2e8f0',
      }}
    >
      {isLoading ? (
        <Grid container spacing={3} sx={{ minHeight: '300px', alignItems: 'stretch' }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  height: '100%',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <NoReportCard />
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
          {/* Summary Cards Section */}
          <Grid item xs={12} lg={6} xl={3}>
            <Card
              sx={{
                height: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <Stack
                direction={isMobile ? 'row' : 'column'}
                sx={{
                  height: '100%',
                  '& > *:not(:last-child)': {
                    borderRight: isMobile ? '1px solid #e0e0e0' : 'none',
                    borderBottom: !isMobile ? '1px solid #e0e0e0' : 'none',
                  },
                }}
              >
                <Card
                  sx={{
                    flex: 1,
                    boxShadow: 'none',
                    border: 'none',
                    borderRadius: 0,
                    height: '100%',
                  }}
                >
                  {postEngagementSummary.totalPosts?.card_type !== 'no-report' && postEngagementSummary?.totalPosts ? (
                    <SummaryCommonCard
                      title={postEngagementSummary.totalPosts?.title}
                      data={postEngagementSummary.totalPosts?.data}
                      percentage={postEngagementSummary.totalPosts?.percentage}
                      percentageState={postEngagementSummary.totalPosts?.percentage_state}
                      averageData={postEngagementSummary.totalPosts?.average_data}
                    />
                  ) : (
                    <NoReportCard />
                  )}
                </Card>

                <Card
                  sx={{
                    flex: 1,
                    boxShadow: 'none',
                    border: 'none',
                    borderRadius: 0,
                    height: '100%',
                  }}
                >
                  {postEngagementSummary.totalEngagement?.card_type !== 'no-report' &&
                  postEngagementSummary?.totalEngagement ? (
                    <SummaryCommonCard
                      title={postEngagementSummary.totalEngagement?.title}
                      data={postEngagementSummary.totalEngagement?.data}
                      percentage={postEngagementSummary.totalEngagement?.percentage}
                      percentageState={postEngagementSummary.totalEngagement?.percentage_state}
                      averageData={postEngagementSummary.totalEngagement?.average_data}
                    />
                  ) : (
                    <NoReportCard />
                  )}
                </Card>
              </Stack>
            </Card>
          </Grid>

          {/* Best Post Section */}
          <Grid item xs={12} lg={6} xl={3}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                p: 2,
              }}
            >
              {postEngagementSummary.bestPost?.card_type !== 'no-report' && postEngagementSummary?.bestPost ? (
                <BestPostCardDetails
                  title={postEngagementSummary.bestPost?.title}
                  comments={postEngagementSummary.bestPost?.comment_count}
                  createdTime={postEngagementSummary.bestPost?.created_time}
                  description={postEngagementSummary.bestPost?.description}
                  engagement={postEngagementSummary.bestPost?.engagement}
                  engagementRate={postEngagementSummary.bestPost?.engagement_rate}
                  mediaUrl={postEngagementSummary.bestPost?.media_url}
                  link={postEngagementSummary.bestPost?.link}
                  reactions={postEngagementSummary.bestPost?.reaction}
                  shares={postEngagementSummary.bestPost?.share_count}
                  reach={postEngagementSummary.bestPost?.reach}
                  saved={postEngagementSummary.bestPost?.saved}
                />
              ) : (
                <NoReportCard />
              )}
            </Card>
          </Grid>

          {/* Chart Section */}
          <Grid item xs={12} xl={6}>
            <Card
              sx={{
                height: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                p: 2,
              }}
            >
              {postEngagementSummary.engagementPostType?.card_type !== 'no-report' &&
              postEngagementSummary?.engagementPostType ? (
                <TableDonutChart
                  title={postEngagementSummary.engagementPostType?.title}
                  engagementPostType={postEngagementSummary.engagementPostType?.engagement_by_type}
                  value={postEngagementSummary.engagementPostType?.value}
                  labels={postEngagementSummary.engagementPostType?.key}
                />
              ) : (
                <NoReportCard />
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

export default EngagementNestedCard;
